/**
 * AN INVOICE'S DOCUMENTS (ops #76 O3, `docs/seams/home.md` section 11.2): the invoice at its first
 * send, a cancellation when a numbered invoice is voided, and a receipt for every payment, each
 * rendered once as a PDF and kept, never rendered again. A `documents` row is the document from the
 * press on (migration `0034`); its bytes are a `files` row with their SHA-256 once they exist. What a
 * document is and where it stands are the two lists in `enums.ts` (`DOCUMENT_KINDS`,
 * `DOCUMENT_STATES`), which the CHECKs are built from; this file holds what the rendering and the
 * keeping read beside them, shared so the office's screen and the Worker never disagree.
 */
import type { DocumentKind, PaymentMethod } from './enums.ts';

/**
 * THE LAYOUT EACH KIND IS RENDERED WITH TODAY, written on the row at the press as
 * `documents.template_version` and never at the render, so every attempt at one document renders the
 * same bytes whatever was deployed in between. A changed layout is a new version here, for documents
 * pressed after it; a document already pending keeps the version it was pressed with, and one already
 * rendered is never rendered again. No CHECK holds the column to this map, because a version added
 * later would then be a rebuild (`docs/gotchas.md` entry 34): the renderer refuses a version it does
 * not know as a failure instead. `refund` and `statement` are kinds with no layout yet (phase 4).
 */
export const DOCUMENT_TEMPLATES = Object.freeze({
  invoice: 'invoice.1',
  cancellation: 'cancellation.1',
  receipt: 'receipt.1',
} as const satisfies Partial<Record<DocumentKind, string>>);
export type TemplatedDocumentKind = keyof typeof DOCUMENT_TEMPLATES;

/**
 * HOW LONG A DOCUMENT IS KEPT AT LEAST: seven years from the day it was issued. An invoice and a
 * receipt are tax records, kept for the life of the business; `files.retained_until` is
 * `documents.issued_on` plus this, the accountant's minimum written at render, and never an expiry:
 * nothing deletes a file for its age. Since ops #82 (`docs/plan-household-forget.md` D11) no forget
 * reads it either: an issued document is kept as issued for good. Seven is the recommendation the
 * owner was asked to confirm on ops #76 (or give the accountant's figure); a different answer is a
 * change to this one constant and one audited update of the rows already kept.
 */
export const DOCUMENT_FLOOR_YEARS = 7;

/** The business as every document names it: its legal name, its mail and its website. */
export const BUSINESS_LEGAL_NAME = 'Faithfully Kept LLC';
export const BUSINESS_EMAIL = 'hello@faithfullykept.com';
export const BUSINESS_SITE = 'faithfullykept.com';

/**
 * THE MOST LINES ONE INVOICE PRINTS. A document is one page, and an invoice with more lines than fit
 * is refused by the renderer with a failure that says so: never a second page, and never a line cut.
 * Twenty-four is what the page holds with the longest address the setting accepts.
 */
export const DOCUMENT_LINES_MAX = 24;

/**
 * HOW A RECEIPT SAYS SHE PAID, by `payments.method`: the one place these words are spelled, so the
 * office's screen and the PDF never disagree. `other` is a payment ops has no better word for.
 */
export const PAYMENT_METHOD_WORDS: Readonly<Record<PaymentMethod, string>> = Object.freeze({
  card: 'Card',
  bank_transfer: 'Bank account',
  zelle: 'Zelle',
  check: 'Check',
  cash: 'Cash',
  other: 'Payment',
});

/**
 * WHAT A CLIENT IS TOLD WHILE A DOCUMENT IS STILL BEING MADE, word for word as `docs/seams/home.md`
 * section 11.2 prints it: home shows it while `ready` is false, home's door refuses a press for it
 * `409 document_not_ready` in these words, and her link's page says the same. The one place it is
 * spelled.
 */
export const DOCUMENT_BEING_MADE = 'Your PDF is being made. It will be here in a few minutes.';

/**
 * WHAT HER LINK'S PAGE SAYS OF HER DOCUMENTS (ops #76 O8, `docs/seams/home.md` section 11.2): the
 * block under her invoice, its button for the invoice and one for each receipt, what the button says
 * while the press is answered, and what she is told when the link could not be had, word for word as
 * home says it on her page there, so the two pages say one thing. The one place they are spelled;
 * `DOCUMENT_BEING_MADE` stands beside them while a PDF is not ready.
 */
export const DOCUMENT_PAGE_WORDS = Object.freeze({
  heading: 'Documents',
  invoice: 'Download the invoice (PDF)',
  receipt: (number: string): string => `Receipt ${number} (PDF)`,
  getting: 'Getting your PDF',
  notGot: 'We could not get the PDF just now. Try again in a minute, or call (727) 337-7775.',
});

/**
 * THE FILE NAME A DOCUMENT IS HANDED OUT UNDER, exactly as `docs/seams/home.md` section 11.2 has it,
 * written on the row once at the press: `Faithfully-Kept-invoice-FK-42.pdf`,
 * `Faithfully-Kept-receipt-FK-R7.pdf`, and `Faithfully-Kept-invoice-FK-41-cancelled.pdf` for the
 * cancellation, which carries its invoice's number.
 */
export function documentFileName(kind: TemplatedDocumentKind, number: string): string {
  if (kind === 'receipt') return `Faithfully-Kept-receipt-${number}.pdf`;
  if (kind === 'cancellation') return `Faithfully-Kept-invoice-${number}-cancelled.pdf`;
  return `Faithfully-Kept-invoice-${number}.pdf`;
}

/**
 * THE FACTS A DOCUMENT IS RENDERED FROM, and nothing else: `documents.facts_json`, written at the
 * press from the rows as they stood, so a document pending on the tick renders the same as it would
 * have straight after the answer, and every attempt at it the same bytes. Days are `YYYY-MM-DD`,
 * money is whole cents, and every name is the row's own words.
 *
 * `business.addressLines` is the one fact not fixed at the press: the press writes null, and the
 * render's first claim writes the owner's setting `document_address` into it, so the row records
 * what was printed. No document is rendered while it is null or empty.
 *
 * One of `invoice`, `receipt` and `cancellation` is present, the one its template version draws.
 */
export interface DocumentFacts {
  readonly v: 1;
  readonly business: DocumentBusiness;
  readonly invoice?: InvoiceFacts;
  readonly receipt?: ReceiptFacts;
  readonly cancellation?: CancellationFacts;
}

export interface DocumentBusiness {
  readonly name: string;
  readonly addressLines: readonly string[] | null;
  readonly email: string;
  readonly site: string;
  readonly phone: string;
}

/** The home a visit was at, as the invoice prints it. */
export interface DocumentHome {
  readonly label: string | null;
  readonly line1: string;
  readonly line2: string | null;
  readonly town: string;
  readonly state: 'FL';
  readonly postalCode: string;
}

/** One line of an invoice, as the client was billed it. */
export interface DocumentLine {
  readonly position: number;
  readonly description: string;
  readonly quantity: number;
  readonly unitPriceCents: number;
  readonly amountCents: number;
}

export interface InvoiceFacts {
  readonly number: string;
  readonly issuedOn: string;
  readonly dueOn: string;
  /** The published terms, in days from the invoice: seven. */
  readonly termsDays: number;
  /** The voided invoice of the same visit this one replaces, by its number, or null. */
  readonly replacesNumber: string | null;
  readonly visitDate: string;
  /** The household's name, and the person the money is asked of, when there is one. */
  readonly household: string;
  readonly namedContact: string | null;
  readonly home: DocumentHome;
  readonly lines: readonly DocumentLine[];
  readonly totalCents: number;
  readonly paidCents: number;
  readonly balanceCents: number;
  readonly zelle: { readonly tag: string; readonly name: string; readonly memo: string };
}

/** One invoice a receipt's payment was applied to. */
export interface ReceiptApplied {
  /** Null for an invoice sent before ops numbered its invoices: the receipt names its day instead. */
  readonly invoiceNumber: string | null;
  readonly invoiceIssuedOn: string;
  readonly visitDate: string;
  readonly homeLabel: string | null;
  readonly amountCents: number;
  readonly balanceAfterCents: number;
}

export interface ReceiptFacts {
  readonly number: string;
  readonly receivedOn: string;
  readonly method: PaymentMethod;
  readonly amountCents: number;
  readonly tipCents: number;
  readonly household: string;
  readonly namedContact: string | null;
  readonly applied: readonly ReceiptApplied[];
  readonly unappliedCents: number;
}

/** The invoice as it was issued, and the day it was cancelled. */
export interface CancellationFacts extends InvoiceFacts {
  readonly cancelledOn: string;
}
